import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, TestTube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { buildErrorToast } from '@/lib/apiError';
import {
  getTestModeConfig,
  updateTestModeConfig,
  type ITestModeConfig,
} from '@/api/daily-report.api';

export function DailyReportTestModeSection() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ITestModeConfig>({ testModeEnabled: true, testModeRecipient: '' });
  const [showConfirm, setShowConfirm] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const { data: config, isLoading } = useQuery({
    queryKey: ['daily-report-test-mode'],
    queryFn: getTestModeConfig,
  });

  useEffect(() => {
    if (config) {
      setForm(config);
    }
  }, [config]);

  const saveMutation = useMutation({
    mutationFn: () => updateTestModeConfig(form),
    onSuccess: () => {
      toast({ title: 'Test mode config saved' });
      setEditing(false);
      setShowConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['daily-report-test-mode'] });
    },
    onError: (err) => buildErrorToast(toast)(err, 'Failed to save'),
  });

  /** BA §S29: Validate email RFC format */
  function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /** BA §S29: Show confirm modal when turning OFF test mode (was ON before) */
  function handleSave() {
    // Validate email when test mode is enabled
    if (form.testModeEnabled && form.testModeRecipient) {
      if (!isValidEmail(form.testModeRecipient)) {
        setEmailError('Please enter a valid email address');
        return;
      }
    }
    setEmailError(null);

    const wasPreviouslyEnabled = config?.testModeEnabled === true;
    const isNowDisabled = !form.testModeEnabled;

    if (wasPreviouslyEnabled && isNowDisabled) {
      setShowConfirm(true);
    } else {
      saveMutation.mutate();
    }
  }

  if (isLoading) {
    return <p className="text-muted-foreground py-2">Loading...</p>;
  }

  return (
    <div className="rounded-lg border p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold flex items-center gap-2">
          <TestTube className="h-4 w-4 text-amber-500" />
          Daily Report — Test Mode
        </h3>
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>

      {config?.testModeEnabled && (
        <div className="flex items-start gap-2 rounded bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <strong>Test mode is ON.</strong> All daily report emails will be redirected to the test recipient
            instead of real client contacts.
          </div>
        </div>
      )}

      {editing ? (
        <div className="max-w-md space-y-3">
          <div className="flex items-center justify-between">
            <Label>Test Mode Enabled</Label>
            <Switch
              checked={form.testModeEnabled}
              onCheckedChange={(checked) => setForm({ ...form, testModeEnabled: checked })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="testRecipient">Test Recipient Email</Label>
            <Input
              id="testRecipient"
              type="email"
              value={form.testModeRecipient}
              onChange={(e) => { setForm({ ...form, testModeRecipient: e.target.value }); setEmailError(null); }}
              placeholder="test@example.com"
              className={emailError ? 'border-red-500' : ''}
            />
            {emailError ? (
              <p className="text-xs text-red-500">{emailError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                When test mode is on, this address receives all daily reports instead of the real recipients.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outline" onClick={() => { setEditing(false); if (config) setForm(config); }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm max-w-md">
          <div>
            <p className="text-muted-foreground">Test Mode</p>
            <p className="font-medium">{config?.testModeEnabled ? 'ON' : 'OFF'}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Test Recipient</p>
            <p className="font-medium">{config?.testModeRecipient || '—'}</p>
          </div>
        </div>
      )}

      {/* BA §S29: Confirm modal when turning OFF test mode */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Turn Off Test Mode
            </DialogTitle>
            <DialogDescription>
              You are about to send daily report emails to <strong>real client contacts</strong> instead of the test recipient. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Yes, Turn Off Test Mode'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
